import { type ReactNode } from 'react';
import { useSnapshot } from 'valtio';

import * as navigationStore from '@cartwhl/common/stores/navigationStore';
import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getAppletsUrl, getHomepageUrl } from '@cartwhl/common/utils';
import {
  formatOrgCreditsForDisplay,
  getOrgAvailableCredits,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import LazyVideo from '@cartwhl/ui/atoms/LazyVideo';
import Text from '@cartwhl/ui/base/TextNew';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import LightDarkIcon from '@cartwhl/ui/icons/LightDarkIcon';
import LikeIcon from '@cartwhl/ui/icons/LikeIcon';
import LogoutIcon from '@cartwhl/ui/icons/LogoutIcon';
import PenIcon from '@cartwhl/ui/icons/PenIcon';
import UserIcon from '@cartwhl/ui/icons/UserIcon';

import SpinIcon from '../icons/SpinIcon';
import Button from '../input/Button';

export const CDN_BASE = 'https://resources.getcartwheel.com/share/demo-videos';
const USER_MENU_DROPDOWN_Z_INDEX = 60;

/** Same clips as homepage `ProductsSection` (`apps/homepage/.../ProductsSection.tsx`). */
const HOMEPAGE_APP_PREVIEW_VIDEO = {
  comic: `${CDN_BASE}/01_home_page/03_platform_capabilities_section/02_comic_update.webm`,
  swing: `${CDN_BASE}/v2_homepage_platform_capabilities_03_swing.webm`,
} as const;

interface UserMenuProps {
  inModal?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /**
   * Replaces the default circular avatar trigger. Must be a single element
   * Radix can merge into (e.g. `<button type="button">`).
   */
  trigger?: ReactNode;
}

export default function UserMenu({
  inModal = false,
  side = 'bottom',
  align = 'end',
  trigger: triggerOverride,
}: UserMenuProps) {
  const { userInfo, theme, organizationInfo } = useSnapshot(userStore.state);

  const orgCreditsWorkspace = isOrgCreditsWorkspace(organizationInfo);
  const orgAvailableCredits = getOrgAvailableCredits(organizationInfo);

  const defaultTrigger = (
    <div className="hover:bg-gray-15 data-[state=open]:bg-gray-15 hover:dark:bg-gray-75 data-[state=open]:dark:bg-gray-90 outline-hidden flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors">
      <Avatar userInfo={userInfo} />
    </div>
  );

  return (
    <Dropdown
      inModal={inModal}
      side={side}
      align={align}
      zIndex={inModal ? undefined : USER_MENU_DROPDOWN_Z_INDEX}
      trigger={triggerOverride ?? defaultTrigger}
    >
      <div className="styled-scrollbar max-h-[calc(var(--radix-dropdown-menu-content-available-height)-1rem)] overflow-y-auto overflow-x-hidden overscroll-contain">
        <div className="hover:bg-gray-15 hover:dark:bg-gray-90 m-2 h-[120px] rounded-md">
          <a
            className="relative flex h-full w-full flex-col items-center justify-center"
            href={getHomepageUrl('/profile/account-details')}
          >
            <Avatar userInfo={userInfo} size={12} className="relative mb-3">
              <span className="dark:bg-gray-90 absolute -bottom-1 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white">
                <PenIcon />
              </span>
            </Avatar>
            <Text token="14/medium" className="">
              {userInfo.firstName} {userInfo.lastName}
            </Text>
            <Text token="12" className="text-gray-45">
              {userInfo.email}
            </Text>
            <Text token="12" className="text-gray-45">
              {userInfo.planType}
            </Text>
          </a>
        </div>
        <div className="flex items-center justify-center">
          <a
            href={getHomepageUrl('/profile/plan-billing')}
            className="border-primary hover:bg-gray-15 inline-flex h-10 items-center gap-2 rounded-md border px-3 py-2"
          >
            <SpinIcon className="h-5 w-5 shrink-0" />
            <Text token="14/medium" leading="title">
              {orgCreditsWorkspace
                ? `${formatOrgCreditsForDisplay(orgAvailableCredits)} credits`
                : `${userInfo.spinsAvailable} Spins`}
            </Text>
          </a>
        </div>
        <div className="mb-3 mt-2 flex items-center justify-center">
          {orgCreditsWorkspace ? (
            <a
              href={getHomepageUrl('/pricing#pricing-credits-explainer')}
              className="text-gray-45 underline"
            >
              <Text token="12">How credits work</Text>
            </a>
          ) : (
            <button
              className="text-gray-45 cursor-pointer underline"
              onClick={() => {
                navigationStore.setModal(
                  navigationStore.CommonModalName.EXPLAIN_SPINS,
                );
              }}
            >
              <Text token="12">What are Spins?</Text>
            </button>
          )}
        </div>
        {organizationInfo?.inStripeFreeTrialPeriod === true && (
          <>
            {/* <Dropdown.Item
              disabled={payNowLoading}
              icon={
                <BlueDotIcon className="text-blue transition-colors group-hover:text-white" />
              }
              variant="blue"
              onClick={async () => {
                if (payNowLoading) return;
                setPayNowLoading(true);
                try {
                  await updatedProductStore.completeStripeTrialEarly();
                } finally {
                  setPayNowLoading(false);
                }
              }}
            >
              {payNowLoading ? 'Processing…' : 'Upgrade'}
            </Dropdown.Item> */}
            <Button
              variant="primary"
              className="w-full"
              onClick={() => {
                updatedProductStore.openActivateEarlyTrialModal();
              }}
            >
              Activate now
            </Button>
          </>
        )}
        {/* {userInfo.planType !== 'unlimited' && (
          <>
            <Dropdown.Item
              href={getHomepageUrl('/pricing')}
              icon={
                <BlueDotIcon className="text-blue transition-colors group-hover:text-white" />
              }
              variant="blue"
            >
              {userInfo.planType === 'free'
                ? 'Upgrade'
                : userInfo.planType === 'pro'
                  ? 'Upgrade to Advanced'
                  : 'Unlimited Plan'}
            </Dropdown.Item>

            <Dropdown.Separator />
          </>
        )} */}
        <Dropdown.Separator />
        <Dropdown.Sub
          text="Apps"
          textClassName="text-tertiary"
          suffix={
            <Text
              token="12/medium"
              className="text-gray-90 bg-yellow flex h-6 items-center justify-center rounded-lg px-2"
            >
              New!
            </Text>
          }
          zIndex={inModal ? undefined : USER_MENU_DROPDOWN_Z_INDEX}
          contentClassName="flex min-w-[288px] max-w-[min(100vw-2rem,380px)] flex-col gap-1"
        >
          <AppItem
            videoSrc={HOMEPAGE_APP_PREVIEW_VIDEO.comic}
            title="Comic"
            subtitle={'AI Video\n Motion Capture'}
            to={`${getAppletsUrl()}/comic`}
          />
          <AppItem
            videoSrc={HOMEPAGE_APP_PREVIEW_VIDEO.swing}
            title="Swing"
            subtitle={'Motion Generation'}
            to={`${getAppletsUrl()}/generation`}
          />
          <AppItem
            src={`${CDN_BASE}/mascot.jpg`}
            title="Mascot"
            subtitle={'Video character\nreplacement'}
            to={`${getAppletsUrl()}/mascot`}
          />
          {/* <AppItem
          src={`${CDN_BASE}/hermes.jpg`}
          title="Hermes"
          subtitle={'Generative editing &\nblending'}
          to={`${getAppletsUrl()}/motion-editor`}
        /> */}
          <AppItem
            src={`${CDN_BASE}/cartographer.jpg`}
            title="Cartographer"
            subtitle={'Universal motion\nretargeting'}
            to={`${getAppletsUrl()}/cartographer`}
          />
        </Dropdown.Sub>
        <Dropdown.Separator />
        <Dropdown.Sub
          text="Theme"
          icon={<LightDarkIcon />}
          zIndex={inModal ? undefined : USER_MENU_DROPDOWN_Z_INDEX}
        >
          <Dropdown.Item
            icon={theme === 'light' ? <CheckmarkIcon /> : null}
            onClick={() => userStore.setTheme('light')}
          >
            Light
          </Dropdown.Item>
          <Dropdown.Item
            icon={theme === 'dark' ? <CheckmarkIcon /> : null}
            onClick={() => userStore.setTheme('dark')}
          >
            Dark
          </Dropdown.Item>
          <Dropdown.Item
            icon={theme === null ? <CheckmarkIcon /> : null}
            onClick={() => userStore.setTheme(null)}
          >
            System
          </Dropdown.Item>
        </Dropdown.Sub>
        <Dropdown.Item
          icon={<LikeIcon />}
          href={getHomepageUrl('/profile/favorites')}
        >
          Favorites
        </Dropdown.Item>
        <Dropdown.Item href={getHomepageUrl('/profile')} icon={<UserIcon />}>
          Account
        </Dropdown.Item>
        <Dropdown.Separator />
        <Dropdown.Item href={getHomepageUrl('/logout')} icon={<LogoutIcon />}>
          Logout
        </Dropdown.Item>
      </div>
    </Dropdown>
  );
}
function AppItem(
  props:
    | {
        videoSrc: string;
        title: string;
        subtitle: string;
        to: string;
      }
    | {
        src: string;
        title: string;
        subtitle: string;
        to: string;
      },
) {
  const { title, subtitle, to } = props;
  const preview =
    'videoSrc' in props ? (
      <div className="bg-gray-15 h-13 w-8 shrink-0 overflow-hidden rounded-sm">
        <LazyVideo
          src={props.videoSrc}
          className="h-full w-full"
          mode="autoplay"
        />
      </div>
    ) : (
      <img
        src={props.src}
        alt=""
        className="h-13 w-8 shrink-0 rounded-sm object-cover"
        loading="lazy"
      />
    );

  return (
    <a
      href={to}
      className="hover:bg-gray-15 flex h-[70px] items-center gap-5 rounded-lg px-3.5 py-2 transition-colors"
    >
      {preview}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Text token="14/medium" className="text-primary">
          {title}
        </Text>
        <Text token="14/medium" className="text-tertiary whitespace-pre-line">
          {subtitle}
        </Text>
      </div>
    </a>
  );
}
